import React, { useMemo } from 'react';
import Activity, { ActivityContainerProps } from '../../../interfaces/activityInterfaces';
import {
	WaitForDependencyTimeOptions,
	PreconditionS3Type,
	preconditionWaitforDependencyLabel,
	Region,
} from '../../../constants/hammerstoneConstants';
import { getMaxRetries, isPrecondWaitTimeValid, objToOptions } from 'src/commons';
import { ColumnLayout } from '@amzn/awsui-components-console';
import Content, { Rules, useViewContent } from 'src/components/helpers/content/';
import { AttributeEditorInput } from 'src/interfaces/inputInterfaces';
import { useFormContext } from 'react-hook-form';
import PAGES from 'src/nav/Pages';
import InternalLink from 'src/components/helpers/InternalLink';
import { DisplayMode } from 'src/components/helpers/content/contentInterfaces';
import { ColumnContainer, HelpPanelInfoLink } from 'src/components/helpers';
import DownstreamActivityPreconditions from './DownstreamActivityPreconditions';
import GroupNameAccess from 'src/components/helpers/GroupNameAccess';

const ACTIVITY_PRECONDITION_S3_KEY_PATTERN =
	/^s3:\/\/hammerstone.*\/confidence_files\/(?<groupName>[^\/]+)\/(?<pipelineName>[^\/]+)\/(?<activityName>[^\/]+)\//;

/**
 * An editable container with information about an Activity's preconditions
 */
export default function PreconditionsContainer(props: ActivityContainerProps) {
	const isView = props.mode === DisplayMode.View;

	// Memoized at top level to avoid unnecessary computations
	const PRECONDITION_OPTIONS = useMemo(() => objToOptions(PreconditionS3Type), []);
	const REGION_OPTIONS = useMemo(() => objToOptions(Region), []);
	const DEPENDENCY_OPTIONS = useMemo(
		() =>
			WaitForDependencyTimeOptions.map((option) => {
				if (!isPrecondWaitTimeValid(option.timeInMin)) {
					return {
						value: option.value,
						label: option.label,
						disabled: true,
						disabledReason:
							"We're changing the maximum allowable values for the precondition wait time to be 1 day.",
					};
				}
				return { value: option.value, label: option.label };
			}),
		[]
	);
	const contentProps = {
		mode: props.mode,
		resourceType: 'activity',
		resourceId: props.activityId,
		diffResourceId: props.diffActivityId,
	} as const;

	const form = useFormContext<Activity>();
	const useStoreValueProps = {
		resourceType: 'activity' as const,
		resourceId: props.activityId,
		path: 'config.PRECON_WAIT' as const,
		mode: props.mode,
		label: preconditionWaitforDependencyLabel,
	} as const;
	const storedWaitForDependencyIndex = useViewContent<'activity'>(useStoreValueProps).value;
	/**
	 * Default waitForDependencyIndex use saved config.PRECON_WAIT in Redux store.
	 * If it is undefined, use 1 as default value.
	 */
	const waitForDependencyIndex = form?.watch('config.PRECON_WAIT') || storedWaitForDependencyIndex || 1;
	const retryMaximum = useMemo(() => getMaxRetries(waitForDependencyIndex), [waitForDependencyIndex]);

	return (
		<ColumnContainer
			header={props.header}
			info={<HelpPanelInfoLink helpPanel="Preconditions" />}
			footer={
				// Only render downstream activities when viewing an activity
				isView && <DownstreamActivityPreconditions activityId={props.activityId} />
			}
		>
			<ColumnLayout columns={3} variant="text-grid">
				<Content.Select
					{...contentProps}
					label="S3 bucket region"
					path="s3PreconRegion"
					options={REGION_OPTIONS}
					filteringType="auto"
					rules={(l) => ({ required: Rules.required(l) })}
				/>
				<Content.Select
					{...contentProps}
					label={preconditionWaitforDependencyLabel}
					path="config.PRECON_WAIT"
					options={DEPENDENCY_OPTIONS}
					rules={(l) => ({
						required: Rules.required(l),
						validate: (value) => {
							return (
								!DEPENDENCY_OPTIONS[value]?.disabled ||
								DEPENDENCY_OPTIONS[value]?.disabledReason ||
								'This option is disabled'
							);
						},
					})}
				/>
				<Content.Number
					{...contentProps}
					label={`Retry (maximum is ${retryMaximum} based on your Wait for dependencies)`}
					path="config.PRECON_RETRY"
					rules={(l) => ({
						required: Rules.required(l),
						min: Rules.min(0, l),
						max: Rules.max(retryMaximum, l),
						validate: {
							int: Rules.isInteger(l),
						},
					})}
				/>
			</ColumnLayout>
			<Content.AttributeEditor
				{...contentProps}
				label="Upstream dependencies (preconditions)"
				path="preconditions"
				missingText="This activity does not have any upstream dependencies (preconditions)."
				definitions={[
					{
						key: 'key',
						label: 'S3 Type',
						input: AttributeEditorInput.Select,
						options: PRECONDITION_OPTIONS,
						defaults: 's3Key',
						rules: { required: Rules.required('Precondition S3 type') },
					},
					{
						key: 'value',
						label: 'S3 Precondition',
						input: AttributeEditorInput.Text,
						placeholder: 's3://<bucket-name>/<folder>/<filename>',
						viewTransform: (value, item) => {
							const activityId = item?.activityId;
							const match = value.match(ACTIVITY_PRECONDITION_S3_KEY_PATTERN);
							if (activityId && match) {
								// Parse the activity's groupName from the S3 path
								const preconGroupName = match?.groups?.groupName;
								return (
									<GroupNameAccess groupName={preconGroupName}>
										<InternalLink
											href={PAGES.VIEW_ACTIVITY.path}
											pathParams={{ activityId }}
											groupName={preconGroupName}
										>
											{value}
										</InternalLink>
									</GroupNameAccess>
								);
							} else {
								return value;
							}
						},
						rules: {
							required: Rules.required('Precondition S3 value'),
							pattern: Rules.pattern.s3Path('Precondition S3 value'),
						},
					},
				]}
			/>
		</ColumnContainer>
	);
}
